import React, { useEffect, useRef, useState } from "react";
import { useGlobal } from "@/context/GlobalContext";
import { withBase } from "@/utils/basePath";

interface MotivationalModalProps {
    onClose: () => void;
    onComplete?: () => void;
   
}

export const MotivationalModal: React.FC<MotivationalModalProps> = ({
    onClose,
    onComplete,
    
}) => {
    const { getUser, updateFirstVisit, refreshUser } = useGlobal();
    const user = getUser();
    const [isVisible, setIsVisible] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [step, setStep] = useState(1);
    // The step whose media is on screen. A new step's media is mounted hidden above it and only replaces it once its
    // first frame has loaded: remounting the video on each step showed the empty (blue) container for a moment.
    const [shownStep, setShownStep] = useState(1);
    const maxStep = 4;
    const primaryButtonRef = useRef<HTMLButtonElement>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const canClose = !user || user.firstVisit === false;

    useEffect(() => {
        document.body.style.overflow = "hidden";
        const t = setTimeout(() => {
            setIsVisible(true);
            primaryButtonRef.current?.focus();
        }, 60);
        return () => {
            document.body.style.overflow = "unset";
            clearTimeout(t);
        };
    }, []);

    useEffect(() => {
        primaryButtonRef.current?.focus();
    }, [step]);

    // Auto-play video when step changes
    useEffect(() => {
        if (videoRef.current && step > 1) {
            videoRef.current.currentTime = 0;
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(err => {
                    console.warn("Video autoplay failed:", err);
                });
            }
        }
    }, [step]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (canClose) onClose();
                return;
            }
            if (e.key === "ArrowRight") return setStep((s) => Math.min(maxStep, s + 1));
            if (e.key === "ArrowLeft") return setStep((s) => Math.max(1, s - 1));
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, canClose, maxStep]);

    const onBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && canClose) {
            onClose();
        }
    };

    async function markFirstVisitCompleted() {
        // Verify user is authenticated before attempting API call
        if (!user || !user.applicantId) {
            console.warn("Cannot mark first visit - user not authenticated", user);
            localStorage.setItem("sep:first-visit", "completed");
            return;
        }

        try {

            // Use the GlobalContext method which has proper authentication handling
            await updateFirstVisit();


            // Refresh user data to get the updated firstVisit flag
            await refreshUser();

        } catch (error) {
            console.error("Failed to update first visit status:", error);
            // Even if API fails, update localStorage as fallback
            try {
                localStorage.setItem("sep:first-visit", "completed");
            } catch (storageError) {
                console.warn("Failed to update localStorage:", storageError);
            }
        }
    }

    const handleContinue = async () => {
        // Only mark complete on final step
        if (step < maxStep) {
            setStep((s) => Math.min(maxStep, s + 1));
            return;
        }

        // Trigger fade out animation
        setIsFadingOut(true);
        
        await markFirstVisitCompleted();

        // Wait for fade animation to complete before closing
        setTimeout(() => {
            try {
                if (typeof onComplete === "function") onComplete();
                else onClose();
            } catch (err) {
                console.error("Error invoking onComplete/onClose after marking first visit:", err);
                try { onClose(); } catch { /* ignore */ }
            }
        }, 1200); 
    };

    const handleNext = () => {
        if (step < maxStep) {
            setStep((s) => s + 1);
        } else {
            handleContinue();
        }
    };

    const handleBack = () => setStep((s) => Math.max(1, s - 1));

    // Handle video end - only auto-advance on step 4
    const handleVideoEnded = () => {
        if (step === 4) {
            handleNext();
        }
        // For steps 2 and 3, do nothing - wait for user to click Next/Begin
    };

    // Get step-specific content
    const getStepContent = () => {
        switch (step) {
            case 1:
                return {
                    title: "Welcome to the Student Enrollment Portal",
                    description: "We're excited to help you take the first step toward your educational goals.",
                    buttonText: "Next"
                };
            case 2:
                return {
                    title: "Your Journey Starts Here",
                    description: "The Student Enrollment Portal is built to help you move through the enrollment process as smoothly as possible.",
                    buttonText: "Next"
                };
            case 3:
                return {
                    title: "You're Cleared for Take Off - Let's Get Going!",
                    description: "Select \"Begin\" to start your application process.",
                    buttonText: "Begin"
                };
            case 4:
                return {
                    title: "",
                    description: "",
                    buttonText: "Continue"
                };
            default:
                return {
                    title: "Welcome to the Student Enrollment Portal",
                    description: "We're excited to help you take the first step toward your educational goals.",
                    buttonText: "Next"
                };
        }
    };

    const getMediaSource = (forStep: number): string => {
        switch (forStep) {
            case 1: return withBase("/FirstVisit/Step1.jpg");
            case 2: return withBase("/FirstVisit/Step2.mp4");
            case 3: return withBase("/FirstVisit/Step3.mp4");
            case 4: return withBase("/FirstVisit/Step4.mp4");
            default: return withBase("/FirstVisit/Step1.jpg");
        }
    };

    const renderStepContent = () => {
        // The media on screen, plus the current step's media above it while that is still loading.
        const layers = shownStep === step ? [step] : [shownStep, step];
        // Loaded (or failed, so a missing file cannot hold the previous step on screen): show it.
        const reveal = (layerStep: number) => {
            if (layerStep === step) setShownStep(layerStep);
        };

        return (
            <div className="relative w-full h-full bg-gradient-to-b from-blue-600 to-blue-800">
                {layers.map((layerStep) => {
                    const isCurrent = layerStep === step;
                    return (
                        <div
                            key={layerStep}
                            className={`absolute inset-0 ${layerStep === shownStep ? "" : "opacity-0"}`}
                            aria-hidden={isCurrent ? undefined : true}
                        >
                            {layerStep > 1 ? (
                                <video
                                    ref={isCurrent ? videoRef : undefined}
                                    src={getMediaSource(layerStep)}
                                    className="w-full h-full object-cover"
                                    autoPlay={isCurrent}
                                    muted
                                    playsInline
                                    onLoadedData={() => reveal(layerStep)}
                                    onError={() => reveal(layerStep)}
                                    onEnded={isCurrent ? handleVideoEnded : undefined}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <img
                                    src={getMediaSource(layerStep)}
                                    alt={`Onboarding step ${layerStep}`}
                                    className="w-full h-full object-cover"
                                    onLoad={() => reveal(layerStep)}
                                    onError={(e) => {
                                        // Fallback if image doesn't exist
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        reveal(layerStep);
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const stepContent = getStepContent();

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-1200 ${
                isVisible && !isFadingOut ? "bg-black/70 backdrop-blur-md" : "bg-black/0"
            }`}
            onClick={onBackdrop}
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-title"
            aria-describedby="welcome-desc"
        >
            <div
                className={`ag-shell relative w-full h-[80%] max-w-6xl overflow-hidden transform transition-all duration-1200 ${
                    isVisible && !isFadingOut 
                        ? "opacity-100 translate-y-0 scale-100" 
                        : "opacity-0 translate-y-2 scale-[.98]"
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="moto-splash-container relative h-full w-full">
                    {/* Media Content (PNG or Video) */}
                    <div className="absolute inset-0 z-0">
                        {renderStepContent()}
                    </div>

                    {/* Overlay Controls */}
                    <div className="moto-splash-content z-10 absolute flex flex-col h-full w-full pointer-events-none">
                        {/* Header - Empty but maintains space */}
                        <div className="p-5 pointer-events-auto">
                            <div className="flex items-center justify-end">
                            </div>
                        </div>

                        {/* Large spacer to push content down */}
                        <div className="flex-grow" style={{ flexGrow: 2.4 }} />

                        {/* Welcome Text - Positioned in lower portion - Only show for steps 1-3 */}
                        {step < 4 && (
                            <div className="pointer-events-none px-6 pb-4">
                                <div className="text-center text-black flex flex-col items-center">
                                    {/* Title - Full width */}
                                    <h2 id="welcome-title" className="welcome-title w-full">
                                        {stepContent.title}
                                    </h2>
                                    {/* Description - Constrained width */}
                                    <div className="max-w-2xl">
                                        <p id="welcome-desc" className="welcome-description">
                                            {stepContent.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Smaller spacer before buttons */}
                        <div style={{ flexGrow: 0.05 }} />

                        {/* Footer with navigation controls - Positioned at very bottom */}
                        <div className="px-6 pb-8 pointer-events-auto">
                            {/* Buttons */}
                            <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
                                {/* Back to the previous screen (steps 2-3; the last step is the closing video). */}
                                {step > 1 && step < 4 && (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="ag-btn-secondary"
                                    >
                                        Back
                                    </button>
                                )}
                                {step < 4 && (
                                    <button
                                        ref={primaryButtonRef}
                                        type="button"
                                        onClick={handleNext}
                                        className="ag-btn-primary"
                                        aria-label={stepContent.buttonText}
                                    >
                                        {stepContent.buttonText}
                                    </button>
                                )}
                            </div>

                            {/* Progress Pills - Only show for steps 1-3 */}
                            {step < 4 && (
                                <div className="flex justify-center items-center gap-3">
                                    {[1, 2, 3].map((pillStep) => (
                                        <div
                                            key={pillStep}
                                            className={`progress-pill ${pillStep <= step ? 'active' : ''}`}
                                            aria-label={`Step ${pillStep}${pillStep <= step ? ' - Completed' : ''}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Styles */}
                <style>{`
                    .ag-shell {
                        border-radius: 5px;
                        border: none;
                        box-shadow: 0 20px 60px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.08);
                    }
                    .welcome-title {
                        font-family: 'Inter', sans-serif;
                        font-weight: 700;
                        font-style: normal;
                        font-size: 38px;
                        line-height: 45px;
                        letter-spacing: 0%;
                        text-align: center;
                        margin-bottom: 0.5rem;
                    }
                    .welcome-description {
                        font-family: 'Sora', sans-serif;
                        font-weight: 400;
                        font-style: normal;
                        font-size: 21px;
                        line-height: 30px;
                        letter-spacing: 0%;
                        text-align: center;
                    }
                    .progress-pill {
                        width: 45px;
                        height: 15px;
                        border-radius: 24px;
                        background: rgba(0, 0, 0, 0.2);
                        transition: background 0.3s ease, transform 0.2s ease;
                    }
                    .progress-pill.active {
                        background: #0054C7;
                        transform: scale(1.05);
                    }
                    .ag-btn-primary {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: .5rem;
                        padding: 2rem 11rem;
                        font-weight: 600;
                        color: #111827;
                        border-radius: .75rem;
                        border: none;
                        background: linear-gradient(90deg,#f59e0b,#fbbf24,#fde047);
                        box-shadow: none;
                        transition: transform .16s ease;
                        min-width: 200px;
                        height: 56px;
                        font-family: 'Inter', sans-serif;
                        font-size: 22px;
                        outline: none;
                    }
                    .ag-btn-primary:hover {
                        transform: translateY(-1px) scale(1.01);
                        outline: none;
                    }
                    .ag-btn-primary:focus {
                        outline: none;
                    }
                    .ag-btn-primary:active {
                        outline: none;
                    }
                    .ag-btn-secondary {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0 3rem;
                        height: 56px;
                        font-weight: 600;
                        font-family: 'Inter', sans-serif;
                        font-size: 22px;
                        color: #111827;
                        border-radius: .75rem;
                        border: 2px solid rgba(17, 24, 39, 0.35);
                        background: rgba(255, 255, 255, 0.85);
                        transition: transform .16s ease, background .16s ease;
                    }
                    .ag-btn-secondary:hover {
                        background: #ffffff;
                        transform: translateY(-1px) scale(1.01);
                    }
                    .ag-btn-secondary:focus-visible {
                        outline: 2px solid #0054C7;
                        outline-offset: 2px;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default MotivationalModal;
